/**
 * Activity Logging - Staff Action Audit Trail
 *
 * Lightweight client-side utility for recording staff actions.
 * Logs are written to the activity_logs Appwrite collection via the /api/db proxy.
 * Every log includes WHO, WHAT, WHEN, WHERE for full accountability.
 *
 * Usage:
 *   import { logActivity } from "@/lib/activity-log"
 *   await logActivity({
 *     staffId: userId,
 *     staffName: "Sarah Server",
 *     staffRole: "server",
 *     action: "order_created",
 *     category: "orders",
 *     description: "Created entrée order for Table 5, Suite 204",
 *     entityType: "order",
 *     entityId: orderId,
 *     entityName: "Table 5",
 *     metadata: { items: 3, total: 45.00 },
 *   })
 */

import { databases, DATABASE_ID, COLLECTIONS } from "@/lib/appwrite"
import { ID } from "appwrite"
import { logger } from "@/lib/logger"
import { errText } from "@/lib/errors"

const log = logger.scope("activity-log")

export type ActivityCategory =
  | "sales"       // ticket sales, food sales, refunds
  | "orders"      // order creation, status changes, delivery
  | "seating"     // table assignments, party dispatch
  | "attendance"  // roll call, meal attendance
  | "wellness"    // wellness checks, sick/away
  | "admin"       // staff management, config changes
  | "auth"        // login, logout
  | "kitchen"     // order bumped, marked ready
  | "service"     // room service, in-suite dining
  | "reservation" // reservation create, edit, cancel, confirm
  | "reports"     // report printed/exported (PHI audit trail)

export interface ActivityLogEntry {
  staffId: string
  staffName: string
  staffRole: string
  action: string
  category: ActivityCategory
  description: string
  entityType?: string
  entityId?: string
  entityName?: string
  metadata?: Record<string, unknown>
  route?: string
}

/**
 * Log a staff activity. Fire-and-forget semantics for the caller (never
 * throws, never blocks a shift) — but failures **are** surfaced to
 * `lib/logger.ts`'s structured channel, so ops can see when the audit
 * trail has gaps. A silently-dropped write would be an audit/compliance
 * hole in a regulated care setting.
 */
export async function logActivity(entry: ActivityLogEntry): Promise<void> {
  try {
    await databases.createDocument(DATABASE_ID, COLLECTIONS.ACTIVITY_LOGS, ID.unique(), {
      staff_id: entry.staffId,
      staff_name: entry.staffName,
      staff_role: entry.staffRole,
      action: entry.action,
      category: entry.category,
      description: entry.description,
      entity_type: entry.entityType || null,
      entity_id: entry.entityId || null,
      entity_name: entry.entityName || null,
      metadata: entry.metadata ? JSON.stringify(entry.metadata) : null,
      route: entry.route || (typeof window !== "undefined" ? window.location.pathname : null),
      timestamp: new Date().toISOString(),
    })
  } catch (error) {
    // Audit write failure — structured log, but don't rethrow.
    log.error("activity log write failed", {
      action: entry.action,
      category: entry.category,
      staffId: entry.staffId,
      entityType: entry.entityType,
      entityId: entry.entityId,
      error: errText(error),
    })
  }
}

/**
 * Batch log multiple activities (e.g., attendance for a whole party).
 * Runs in parallel for speed.
 */
export async function logActivities(entries: ActivityLogEntry[]): Promise<void> {
  await Promise.allSettled(entries.map(logActivity))
}
